import React from "react";
import { findSegmentIndex, formatDuration, PLAYBACK_MODES, YOUTUBE_SPEED_OPTIONS } from "../lib/playback";

export function SmartTimeline({ plan, currentTime = 0, onSeek, speedLimits, onSpeedLimit, speedMode, onSpeedMode, cached, onRefresh, processing = false }) {
  if (!plan) {
    return (
      <section className="panel timeline-panel" aria-labelledby="timeline-title">
        <div className="panel-heading"><div><span className="eyebrow">Playback plan</span><h2 id="timeline-title">Smart timeline</h2></div></div>
        <div className="timeline-empty"><span className="empty-pulse" />A playback plan will appear when timed captions are available.</div>
      </section>
    );
  }

  const timelineSegments = plan.segments.map((segment, index) => {
    const midpoint = segment.start + (segment.end - segment.start) / 2;
    const chapter = plan.chapters?.find((item, chapterIndex) => midpoint >= item.start && (midpoint < item.end || (chapterIndex === plan.chapters.length - 1 && midpoint <= item.end)));
    return {
      ...segment,
      title: chapter?.title || segment.reason || `Section ${index + 1}`,
      importance: chapter?.importance || (segment.category === "fast" ? "lighter" : "standard"),
      speedClass: segment.category === "fast" && segment.speed > (speedLimits?.normal ?? 1.25) + 0.01 ? "fast" : "cruise",
    };
  });
  const currentIndex = findSegmentIndex(timelineSegments, currentTime);
  const progress = Math.min(100, Math.max(0, (currentTime / plan.originalDuration) * 100));

  return (
    <section className="panel timeline-panel" aria-labelledby="timeline-title">
      <div className="panel-heading">
        <div><span className="eyebrow">Playback plan</span><h2 id="timeline-title">Smart timeline</h2></div>
        <div className="timeline-heading-actions">
          {cached && <span className="segment-count">Cached</span>}
          {processing && <span className="segment-count" role="status">Updating runtime…</span>}
          {onRefresh && <button type="button" className="timeline-refresh" onClick={onRefresh}>Re-analyze</button>}
          <span className="segment-count">{timelineSegments.length} sections</span>
        </div>
      </div>
      <div className="timeline-mode-options" role="group" aria-label="Adaptive playback pace">
        {Object.entries(PLAYBACK_MODES).map(([key, mode]) => (
          <button type="button" key={key} disabled={processing} className={speedMode === key ? "is-selected" : ""} aria-pressed={speedMode === key} aria-label={`${mode.label} mode: normal sections ${mode.normal} times, fast sections ${mode.fast} times`} onClick={() => onSpeedMode(key)}>
            <strong>{mode.label}</strong><span>{mode.normal}× normal</span><span>{mode.fast}× fast</span>
          </button>
        ))}
      </div>
      <details className="timeline-custom-speeds">
        <summary>Customize speeds{speedMode === "custom" && <span>Custom</span>}</summary>
        <div className="timeline-speed-controls" aria-label="Custom section speeds">
          <label><span>Normal section</span><select disabled={processing} aria-label="Normal section speed" value={speedLimits.normal} onChange={(event) => onSpeedLimit("normal", Number(event.target.value))}>{YOUTUBE_SPEED_OPTIONS.map((rate) => <option key={rate} value={rate}>{rate}×</option>)}</select></label>
          <label><span>Fast section</span><select disabled={processing} aria-label="Fast section speed" value={speedLimits.fast} onChange={(event) => onSpeedLimit("fast", Number(event.target.value))}>{YOUTUBE_SPEED_OPTIONS.map((rate) => <option key={rate} value={rate}>{rate}×</option>)}</select></label>
        </div>
      </details>
      <details className="timeline-how">
        <summary>How speeds are chosen</summary>
        <p>TheoFast reads the captions to decide where to speed up: explanations and caveats get your normal speed; repetition and lighter discussion get your fast speed.</p>
        <p>Pick a mode or set your own speeds. Nothing is automatically skipped.</p>
      </details>
      {plan.speechPace?.limitedSegments > 0 && <p className="pace-note">Rapid speech stays closer to its original pace in {plan.speechPace.limitedSegments} sections.</p>}
      <div className="timeline-track" aria-label={`Playback is ${Math.round(progress)} percent complete`}>
        {timelineSegments.map((segment, index) => {
          const speedClass = segment.speedClass;
          return (
          <button
            type="button"
            key={`${segment.start}-${segment.end}`}
            className={`timeline-block timeline-block--${speedClass} ${index === currentIndex ? "is-current" : ""}`}
            style={{ width: `${((segment.end - segment.start) / plan.originalDuration) * 100}%` }}
            title={`${formatDuration(segment.start)} · ${segment.title} · ${segment.speed}×`}
            aria-label={`Jump to ${segment.title} at ${formatDuration(segment.start)}, ${segment.speed} times speed`}
            onClick={() => onSeek?.(segment.start)}
          />
          );
        })}
        {plan.segments.filter((segment) => segment.sponsor).map((segment) => (
          <span
            key={`sponsor-${segment.start}-${segment.end}`}
            className="timeline-sponsor-marker"
            style={{ left: `${(segment.start / plan.originalDuration) * 100}%`, width: `${((segment.end - segment.start) / plan.originalDuration) * 100}%` }}
            title={`Sponsor · ${formatDuration(segment.start)}–${formatDuration(segment.end)}`}
          />
        ))}
        <span className="timeline-playhead" style={{ left: `${progress}%` }} />
      </div>
      <div className="timeline-legend" aria-label="Timeline legend">
        <span><i className="legend-dot legend-dot--cruise" />Normal speed</span>
        <span><i className="legend-dot legend-dot--fast" />Fast speed</span>
        {plan.segments.some((segment) => segment.sponsor) && <span><i className="legend-dot legend-dot--sponsor" />Sponsor</span>}
      </div>
      <ol className="segment-list" aria-label="All playback sections">
        {timelineSegments.map((segment, index) => {
          const speedClass = segment.speedClass;
          return (
          <li key={`${segment.start}-${segment.end}`} className={index === currentIndex ? "is-current" : ""}>
            <button type="button" className="timeline-chapter-button" onClick={() => onSeek?.(segment.start)}>
              <div className="segment-time"><span>{formatDuration(segment.start)}</span><i /></div>
              <div className="segment-copy"><strong>{segment.title}</strong><span>{formatDuration(segment.end - segment.start)} · {segment.reason}</span></div>
              <span className={`speed-pill speed-pill--${speedClass}`} title={segment.paceCapped ? "Reduced to account for the speaker's measured pace" : undefined}>{segment.speed}×</span>
            </button>
          </li>
          );
        })}
      </ol>
    </section>
  );
}
